import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MEDIA, type MediaType } from "@/mocks/media";
import { CATEGORIES, GENRES_BY_TYPE, REGIONS } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import MediaListItem from "@/components/base/MediaListItem";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import FilterPanel from "./components/FilterPanel";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const meta = CATEGORIES.find((c) => c.type === type);

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("all");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // 切换分类时重置筛选条件
  useEffect(() => {
    setGenre("全部");
    setRegion("全部");
    setYear("all");
    setSort("latest");
    setPage(1);
    setShowFilters(false);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [genre, region, year, sort]);

  usePageMeta({
    title: meta ? `${meta.label} - WWW.17C影视平台` : "分类 - WWW.17C影视平台",
    description: meta
      ? `${meta.description} 在 WWW.17C 浏览最新最全的${meta.label}内容，可按题材、地区、年份与热度筛选。`
      : "浏览 WWW.17C 影视分类内容。",
    keywords: meta ? `${meta.label},${meta.englishName},WWW.17C,影视分类` : "影视分类,WWW.17C",
  });

  const filtered = useMemo(() => {
    if (!meta) return [];
    let list = MEDIA.filter((item) => item.type === (meta.type as MediaType));
    if (genre !== "全部") list = list.filter((item) => item.genres.includes(genre));
    if (region !== "全部") list = list.filter((item) => item.region === region);
    if (year === "classic") list = list.filter((item) => item.year <= 2023);
    else if (year !== "all") list = list.filter((item) => String(item.year) === year);

    const sorted = [...list];
    if (sort === "hot") sorted.sort((a, b) => b.views - a.views);
    else if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    return sorted;
  }, [meta, genre, region, year, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("all");
    setSort("latest");
    setPage(1);
  };

  if (!meta) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-10 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="该分类不存在"
          description="你访问的影视分类无法识别，可能路径有误。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE("/");
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: meta.label }]} />

      {/* 标题区 */}
      <div className="mt-4 rounded-lg bg-primary-950 px-6 py-8 md:px-10 md:py-10">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-500 text-primary-950">
            <i className={`${meta.icon} text-2xl`}></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
              {meta.label}
            </h1>
            <p className="text-xs text-background-400">{meta.englishName}</p>
          </div>
        </div>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-300">{meta.description}</p>
      </div>

      <div className="mt-8 flex flex-col gap-8 lg:flex-row">
        {/* 桌面侧边筛选 */}
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24">
            <FilterPanel
              title={meta.label}
              genreOptions={GENRES_BY_TYPE[meta.type]}
              genre={genre}
              onGenre={setGenre}
              regionOptions={REGIONS}
              region={region}
              onRegion={setRegion}
              year={year}
              onYear={setYear}
              sort={sort}
              onSort={setSort}
              onReset={resetFilters}
            />
          </div>
        </aside>

        {/* 列表区 */}
        <div className="min-w-0 flex-1">
          <div className="mb-5 flex items-center justify-between gap-4">
            <p className="text-sm text-foreground-500">
              共找到 <span className="font-semibold text-accent-600">{filtered.length}</span> 部{meta.label}
            </p>
            <button
              type="button"
              onClick={() => setShowFilters(true)}
              className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 lg:hidden"
            >
              <i className="ri-filter-3-line"></i>
              筛选
            </button>
          </div>

          {pageItems.length === 0 ? (
            <EmptyState
              icon="ri-search-eye-line"
              title="暂无符合条件的影视作品"
              description="试试调整或重置筛选条件，发现更多精彩内容。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          ) : (
            <div className="space-y-4">
              {pageItems.map((item) => (
                <MediaListItem key={item.id} item={item} />
              ))}
            </div>
          )}

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />

          <div className="mt-8 flex flex-wrap gap-3">
            {CATEGORIES.filter((c) => c.type !== meta.type).map((c) => (
              <Link
                key={c.type}
                to={`/category/${c.type}`}
                className="flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className={c.icon}></i>
                浏览{c.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* 移动端筛选抽屉 */}
      {showFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden" onClick={() => setShowFilters(false)}>
          <div className="absolute inset-0 bg-primary-950/50"></div>
          <div
            className="absolute inset-y-0 left-0 w-[85%] max-w-sm overflow-y-auto bg-background-50 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-950">筛选 {meta.label}</span>
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                aria-label="关闭筛选"
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 hover:bg-background-200"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterPanel
              title={meta.label}
              genreOptions={GENRES_BY_TYPE[meta.type]}
              genre={genre}
              onGenre={setGenre}
              regionOptions={REGIONS}
              region={region}
              onRegion={setRegion}
              year={year}
              onYear={setYear}
              sort={sort}
              onSort={setSort}
              onReset={resetFilters}
            />
            <button
              type="button"
              onClick={() => setShowFilters(false)}
              className="mt-4 w-full rounded-md bg-primary-600 py-2.5 text-sm font-medium text-background-50"
            >
              查看结果（{filtered.length}）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}